import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { LocalizeRouterService } from '@gilsdav/ngx-translate-router';
import { TranslateService } from '@ngx-translate/core';
import {
  OrganizationLightInterface,
  SimpleStatRatingInterface,
  SimpleStatDistributionInterface,
} from '@sport-activities/sdk';
import { defaultIfEmpty, finalize, forkJoin, Subscription } from 'rxjs';

import { getActivityDetailRouteName } from '~/attendee/helpers/activity/activity-detail-route-name.helper';
import { BackLinkInterface } from '~/checkout/interfaces/back-link.interface';
import { TipsVariantEnum } from '~/component/tips/enum/variant.enum';
import { RatingSizeEnum } from '~/component/vtmn-rating/enums/RatingSize.enum';
import { ReviewStat } from '~/core/model/review-stats.model';
import { ActivityService } from '~/core/services/activity/activity.service';
import { LoaderService } from '~/core/services/loader/loader.service';
import { OrganizationService } from '~/core/services/organization/organization.service';
import { OrganizerSharedActivityService } from '~/organizer/shared/services/activity/activity.service';
import { VtmnPlayIconTypeEnum } from '../../../../component/vtmn-play-icon/enums/vtmn-play-icon-type.enum';
import { TitleService } from '~/core/services/title/title.service';
import { StatfulReviewFiltersInterface } from '~/core/interfaces/statful-review-filters.interface';

const ORGANIZER_REVIEWS_LOADER_KEY = 'ORGANIZER_REVIEWS_LOADER_KEY';

@Component({
  selector: 'app-attendee-reviews-page',
  templateUrl: './reviews-page.component.html',
  styleUrls: ['./reviews-page.component.scss'],
})
export class AttendeeReviewsPageComponent implements OnInit {
  @Input() public hasLoadMoreButton: boolean;
  @Input() public hasOrganizer: boolean;
  @Input() public hasPagination = false;
  @Input() public reviews = [];
  @Output() public loadMore = new EventEmitter<void>();
  private readonly subscriptions = new Subscription();
  public readonly iconType = VtmnPlayIconTypeEnum;
  public readonly organizerReviewsLoaderKey = ORGANIZER_REVIEWS_LOADER_KEY;
  public readonly ratingSize = RatingSizeEnum;
  public readonly tipsVariant = TipsVariantEnum;
  public activityId: number;
  public activityMedia: string;
  public activityName: string;
  public activityType: string;
  public averageAttributeRating: SimpleStatRatingInterface[];
  public averageRating: number;
  public hasApiError = false;
  public isLoading = true;
  public organisation: OrganizationLightInterface;
  public organizerId: string;
  public organizerReviews = [];
  public organizerReviewsStats;
  public ratingDistribution: SimpleStatDistributionInterface[];
  public recommendationRate: number;
  public reviewCount = 0;
  public reviewsParams = { range: '0-4' };
  private hasOrganizerReviewsEnrichment = false;
  private hasOrganizerReviewsStats = false;
  private organizerReviewsRequestInFlight = false;
  private organizerReviewsRequestId = 0;
  private organizerReviewsRequestOrganizationId: string;

  constructor(
    public readonly activityService: ActivityService,
    public readonly activatedRoute: ActivatedRoute,
    public readonly loaderService: LoaderService,
    public readonly localizeRouterService: LocalizeRouterService,
    public readonly organizerActivityService: OrganizerSharedActivityService,
    private readonly organizationService: OrganizationService,
    private readonly titleService: TitleService,
    private readonly translateService: TranslateService
  ) {}

  public ngOnInit(): void {
    if (this.hasOrganizer === true) {
      // if organizer reviews
      this.activatedRoute?.parent?.params?.subscribe((organizerId) => {
        this.organizerId = organizerId.id;
      });
      this.subscriptions.add(
        this.organizationService
          .getOrganizationById(this.organizerId)
          .subscribe({
            next: (organizer) => {
              this.organisation = organizer;
              this.titleService.setPageTitle('seo.reviews.organizer.title', {
                organizerName: this.organisation?.name,
              });
              this.loaderService.loading$.subscribe(() => {
                this.isLoading = false;
              });
            },
            error: () => {
              this.loaderService.loading$.subscribe(() => {
                this.isLoading = false;
                this.hasApiError = true;
              });
            },
          })
      );
      this.subscriptions.add(
        this.organizationService
          .getOrganizationReviewsStats(this.organizerId)
          .subscribe({
            next: (stats: ReviewStat) => {
              this.organizerReviewsStats = stats;
              this.reviewCount = stats?.count;
              this.averageRating = stats?.averageRating;
              this.averageAttributeRating = stats?.averageAttributeRating;
              this.recommendationRate =
                stats?.recommendedCount / this.reviewCount;
              this.ratingDistribution = this.getRatingDistribution(
                stats?.ratingDistribution
              ).sort((a, b) => {
                return Number(b.code) - Number(a.code);
              });
              this.hasOrganizerReviewsStats = true;
              this.updateOrganizerPagination();

              this.loaderService.loading$.subscribe(() => {
                this.isLoading = false;
              });
            },
            error: () => {
              this.loaderService.loading$.subscribe(() => {
                this.isLoading = false;
                this.hasApiError = true;
              });
            },
          })
      );

      this.subscriptions.add(this.getOrganizationReviews(this.organizerId));
      return;
    }
    // if activity reviews
    this.activatedRoute.params.subscribe((activityId) => {
      this.activityId = Number(activityId.id);
      this.getActivity(this.activityId);
    });
  }

  public get backLink(): BackLinkInterface {
    return {
      path: this.localizeRouterService
        .translateRoute(
          getActivityDetailRouteName(this.activityType, this.activityId)
        )
        .toString(),
    };
  }

  public get hasResults(): boolean {
    return !this.isLoading && !this.hasApiError && this.reviewCount > 0;
  }

  public cssStyle(item: string): string {
    return item.replace(/\s/g, '');
  }

  public getActivity(id: number): void {
    this.activityService.getActivity(id.toString()).subscribe({
      next: (activity) => {
        this.titleService.setPageTitle('seo.reviews.activity.title', {
          activityName: activity?.name,
        });

        this.activityName = activity.name;
        this.activityMedia = activity.image.url;
        this.activityType = activity.type;
        this.getReviews(this.activityId);
      },
      error: () => {
        this.loaderService.loading$.subscribe(() => {
          this.isLoading = false;
          this.hasApiError = true;
        });
      },
    });
  }

  public getOrganizationReviews(
    id: string,
    updateRange?: { [key: string]: unknown }
  ): void {
    if (
      this.organizerReviewsRequestInFlight &&
      this.organizerReviewsRequestOrganizationId === id
    ) {
      return;
    }

    const organizationChanged =
      this.organizerReviewsRequestOrganizationId !== undefined &&
      this.organizerReviewsRequestOrganizationId !== id;
    const requestId = ++this.organizerReviewsRequestId;

    if (organizationChanged) {
      this.organizerReviews = [];
      this.hasOrganizerReviewsEnrichment = false;
      this.hasPagination = false;
      this.hasLoadMoreButton = undefined;
    }

    this.organizerReviewsRequestOrganizationId = id;
    this.reviewsParams = updateRange
      ? this.updateRange(this.reviewsParams)
      : { range: '0-4' };
    this.hasOrganizerReviewsEnrichment = false;
    this.organizerReviewsRequestInFlight = true;
    this.loaderService.start(ORGANIZER_REVIEWS_LOADER_KEY);

    forkJoin({
      activities:
        this.organizerActivityService.getActivitiesByOrganizationId(id),
      reviews: this.organizationService.getOrganizationReviews(
        id,
        this.reviewsParams
      ),
    })
      .pipe(
        defaultIfEmpty(null),
        finalize(() => {
          if (requestId === this.organizerReviewsRequestId) {
            this.organizerReviewsRequestInFlight = false;
            this.loaderService.stop(ORGANIZER_REVIEWS_LOADER_KEY);
          }
        })
      )
      .subscribe({
        next: (result) => {
          if (
            requestId !== this.organizerReviewsRequestId ||
            this.organizerReviewsRequestOrganizationId !== id ||
            this.organizerId !== id
          ) {
            return;
          }

          if (!result?.reviews || !result.activities?.items?.length) {
            this.setOrganizerReviewsApiError();
            return;
          }

          const enrichedReviews = [];
          for (const review of result.reviews) {
            const linkedActivity = result.activities.items.find(
              (activity) => activity.sku === Number(review?.subject?.identifier)
            );

            enrichedReviews.push({
              ...review,
              activityMedia: linkedActivity?.image?.url
                ? linkedActivity.image.url
                : '',
              activityName: linkedActivity?.name ? linkedActivity.name : '',
            });
          }

          this.organizerReviews =
            this.organizerReviews?.concat(enrichedReviews);
          this.hasOrganizerReviewsEnrichment = true;
          this.updateOrganizerPagination();
        },
        error: () => {
          if (
            requestId !== this.organizerReviewsRequestId ||
            this.organizerReviewsRequestOrganizationId !== id ||
            this.organizerId !== id
          ) {
            return;
          }

          this.setOrganizerReviewsApiError();
        },
      });
  }

  private setOrganizerReviewsApiError(): void {
    this.hasOrganizerReviewsEnrichment = false;
    this.hasPagination = false;
    this.hasLoadMoreButton = undefined;
    this.loaderService.loading$.subscribe(() => {
      this.isLoading = false;
      this.hasApiError = true;
    });
  }

  private updateOrganizerPagination(): void {
    if (!this.hasOrganizerReviewsStats || !this.hasOrganizerReviewsEnrichment) {
      return;
    }

    this.hasPagination = this.organizerReviews.length < this.reviewCount;
    this.hasLoadMoreButton = this.hasPagination;
  }

  public getRatingDistribution(
    array: Array<{ code: string; value: number }>
  ): SimpleStatDistributionInterface[] {
    const ratingDistributionArray = [];
    if (typeof array !== 'number') {
      array.forEach((item) => {
        ratingDistributionArray.push({
          code: item.code,
          rate: item.value / this.reviewCount,
        });
      });
    }
    return ratingDistributionArray;
  }

  public getReviews(id: number, updateRange?: object): void {
    this.reviewsParams = updateRange
      ? this.updateRange(this.reviewsParams)
      : { range: '0-4' };
    this.activityService
      .getActivityReviews(id, this.reviewsParams)
      .subscribe((reviews) => {
        if (reviews.length === 0) {
          this.isLoading = false;
          return;
        }
        this.reviews = this.reviews?.concat(reviews);
        this.activityService.getActivityReviewsStats(id).subscribe((stats) => {
          this.reviewCount = stats.count;
          this.averageRating = stats.averageRating;
          this.averageAttributeRating = stats.averageAttributeRating;
          this.recommendationRate = stats.recommendedCount / this.reviewCount;
          this.hasPagination = this.reviews.length < this.reviewCount;
          this.hasLoadMoreButton = this.hasPagination;
          this.ratingDistribution = this.getRatingDistribution(
            stats.ratingDistribution
          ).sort((a, b) => {
            return Number(b.code) - Number(a.code);
          });
        });

        this.loaderService.loading$.subscribe(() => {
          this.isLoading = false;
        });
      });
  }
  public updateRange(actualRangeParam: StatfulReviewFiltersInterface): {
    range: string;
  } {
    const newRange = actualRangeParam.range.split('-').map((str) => {
      return Number(str);
    });
    this.reviewsParams = {
      range: (newRange[0] + 5).toString() + '-' + (newRange[1] + 5).toString(),
    };
    return this.reviewsParams;
  }

  public get reviewsTranslation(): string {
    return this.reviewCount > 1
      ? this.translateService.instant('reviews.number_of_reviews', {
          numberOfReviews: this.reviewCount || 0,
        })
      : this.translateService.instant('reviews.number_of_review', {
          numberOfReviews: this.reviewCount || 0,
        });
  }
}
